import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { Button } from "reactstrap";
import {
    usePagination,
    useTable,
    useRowSelect,
    useGlobalFilter,
} from "react-table";
import BTable from "react-bootstrap/Table";
import { useStep } from "react-hooks-helper";
import { useSelector } from "react-redux";
import { StyledButton } from "@aureskonnect/react-ui";

import { store as vStore } from "@vStore";
import store, { addCustomersCitiesSelectionItem } from "../store";

import { useResource } from "@hooks/useResource";

import { ITEMS_PER_PAGE } from "@constants/index";

import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import AddSvgIcon from "@components/Common/SvgIcons/AddSvgIcon";
import Pagination from "@components/Common/Table/Pagination";

import TitleRowButtons from "../SegmentContent/TitleRowsButtons";
import MySelection from "./MySelection";
import { Styles } from "../Styles";

export default function CustomersCities({ title }: { title: string }) {
    const { t } = useTranslation();

    const { mainColor } = useSnapshot(vStore);
    const advancedStore = useSnapshot(store);
    const selections = useSelector((state: any) => state.selections);

    const { index, navigation } = useStep({ steps: 2 });

    const [customersCitiesList, setCustomersCitiesList] = React.useState<any[]>(
        []
    );

    const {
        resourceData: customersCities,
        error: customersCitiesError,
    } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/transactions/customers-cities${
            selections.designation && selections.designation !== "my_favorites"
                ? `?selected=${selections.designation}`
                : ""
        }`
    );

    function handleMultipleAddingButtonOnClickEvent() {
        selectedFlatRows
            .map((row: any) => row.original)
            .forEach((item: string) => {
                addCustomersCitiesSelectionItem(item);
            });

        setCustomersCitiesList(
            customersCitiesList.filter((customerCity: any) => {
                return !selectedFlatRows
                    .map((row: any) => row.original)
                    .map((s) => s.toLowerCase())
                    .includes(customerCity.toLowerCase());
            })
        );
    }

    const handleAddingCustomersCitiesButtonOnClickEvent = React.useCallback(
        (customerCity: any) => {
            addCustomersCitiesSelectionItem(customerCity);

            setCustomersCitiesList(
                customersCitiesList.filter((datum: any) => {
                    return customerCity.toLowerCase() !== datum.toLowerCase();
                })
            );
        },
        [customersCitiesList]
    );

    const columns: AdvancedTableColumnsType[] = React.useMemo(
        () => [
            {
                Header: t("Designation"),
                accessor: (customerCity: any): string => customerCity,
            },
            {
                Header: "",
                id: "actions",
                disableGlobalFilter: true,
                accessor: function (customerCity: any) {
                    return (
                        <div>
                            <Button
                                type="button"
                                color={mainColor === null ? "secondary" : ""}
                                outline={true}
                                block={true}
                                style={{
                                    width: "127px",
                                    float: "right",
                                    borderColor: mainColor,
                                    color: mainColor,
                                }}
                                className="d-none d-sm-none d-md-block"
                                onClick={() =>
                                    handleAddingCustomersCitiesButtonOnClickEvent(
                                        customerCity
                                    )
                                }
                            >
                                {t("Add")}
                            </Button>
                            <button
                                className="border-0 d-block d-sm-block d-md-none p-1 bg-transparent__clz icon-color__clz"
                                onClick={() =>
                                    handleAddingCustomersCitiesButtonOnClickEvent(
                                        customerCity
                                    )
                                }
                            >
                                <AddSvgIcon />
                            </button>
                        </div>
                    );
                },
            },
        ],
        [t, handleAddingCustomersCitiesButtonOnClickEvent, mainColor]
    );

    const IndeterminateCheckbox = React.forwardRef(
        ({ indeterminate, ...rest }: any, ref: any) => {
            const defaultRef = React.useRef();
            const resolvedRef = ref || defaultRef;

            React.useEffect(() => {
                resolvedRef.current!.indeterminate = indeterminate;
            }, [resolvedRef, indeterminate]);

            let localProps = { ...rest, title: t(rest.title) };

            return <input type="checkbox" ref={resolvedRef} {...localProps} />;
        }
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex },
        selectedFlatRows,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data: customersCitiesList,
            initialState: { pageIndex: 0 },
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <IndeterminateCheckbox
                            {...getToggleAllRowsSelectedProps()}
                        />
                    ),
                    Cell: ({ row }) => (
                        <IndeterminateCheckbox
                            {...row.getToggleRowSelectedProps()}
                        />
                    ),
                    disableGlobalFilter: true,
                },
                ...columns,
            ]);
        }
    );

    React.useEffect(() => {
        setPageSize(ITEMS_PER_PAGE);
    }, [setPageSize]);

    React.useEffect(() => {
        setCustomersCitiesList(
            advancedStore.customersCitiesSelection.length > 0
                ? customersCities.data.filter((customerCity: any) => {
                      return !advancedStore.customersCitiesSelection
                          .map((s) => s.toLowerCase())
                          .includes(customerCity.toLowerCase());
                  })
                : customersCities.data
        );
    }, [customersCities, advancedStore]);

    return (
        <React.Fragment>
            {index === 0 && (
                <React.Fragment>
                    <div className="d-flex justify-content-between">
                        <div className="app-search m-2 p-0">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control w-xs-90"
                                    placeholder={`${t("Search")} ...`}
                                    onChange={(e) =>
                                        setGlobalFilter(e.target.value)
                                    }
                                />
                                <span className="bx bx-search-alt"></span>
                            </div>
                        </div>

                        <div
                            className="d-flex my-2 mx-3"
                            style={{ gap: "10px" }}
                        >
                            <StyledButton
                                rounded={true}
                                outline={false}
                                variant={mainColor === null ? "secondary" : ""}
                                className="white-space-nowrap"
                                style={{
                                    background: mainColor,
                                }}
                                disabled={selectedFlatRows.length === 0}
                                onClick={handleMultipleAddingButtonOnClickEvent}
                            >
                                {t("Add")}
                            </StyledButton>
                            <TitleRowButtons {...navigation} title={title} />
                        </div>
                    </div>
                    {customersCitiesError ? (
                        <ErrorPage />
                    ) : (
                        <CustomErrorBoundary>
                            <Styles>
                                <BTable responsive {...getTableProps()}>
                                    <thead>
                                        {headerGroups.map((headerGroup) => (
                                            <tr
                                                {...headerGroup.getHeaderGroupProps()}
                                            >
                                                {headerGroup.headers.map(
                                                    (column) => {
                                                        return (
                                                            <th
                                                                {...column.getHeaderProps()}
                                                            >
                                                                {column.render(
                                                                    "Header"
                                                                )}
                                                            </th>
                                                        );
                                                    }
                                                )}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                        {page.map((row, _) => {
                                            prepareRow(row);
                                            return (
                                                <tr {...row.getRowProps()}>
                                                    {row.cells.map((cell) => {
                                                        return (
                                                            <td
                                                                className="white-space-nowrap"
                                                                {...cell.getCellProps()}
                                                            >
                                                                {cell.render(
                                                                    "Cell"
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </BTable>
                            </Styles>
                            {customersCitiesList.length > ITEMS_PER_PAGE ? (
                                <Pagination
                                    canPreviousPage={canPreviousPage}
                                    canNextPage={canNextPage}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    gotoPage={gotoPage}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    pageIndex={pageIndex}
                                />
                            ) : null}
                        </CustomErrorBoundary>
                    )}
                </React.Fragment>
            )}
            {index === 1 && (
                <MySelection
                    {...navigation}
                    data={advancedStore.customersCitiesSelection}
                />
            )}
        </React.Fragment>
    );
}
